import React, { useState, useEffect } from 'react';
import { Card, Table, Button, Input, Space, Tag, Typography, Row, Col, Modal, Form, Select, message, Upload, Drawer } from 'antd';
import { SearchOutlined, PlusOutlined, ReloadOutlined, EditOutlined, DeleteOutlined, FileTextOutlined, UploadOutlined, SyncOutlined, LinkOutlined } from '@ant-design/icons';
import { deviceService } from '../../services/deviceService';
import { packageService } from '../../services/packageService';
import { DeviceBatch as DeviceBatchData, DeviceBatchRequest, DeviceType, ImportResult, ImportError, Package } from '../../types';
import PackageGroupSelector from '../../components/PackageGroupSelector';
import { formatPackageDuration } from '../../utils/packageDuration';
import { useAuth } from '../../contexts/AuthContext';
import DealerSelector from '../../components/DealerSelector';
import type { ColumnsType } from 'antd/es/table';

const { Title } = Typography;
const { Search } = Input;
const { Option } = Select;

const DeviceBatch: React.FC = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingRecord, setEditingRecord] = useState<DeviceBatchData | null>(null);
  const [dataSource, setDataSource] = useState<DeviceBatchData[]>([]);
  const [deviceTypes, setDeviceTypes] = useState<DeviceType[]>([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [searchParams, setSearchParams] = useState<any>({});
  const [form] = Form.useForm();
  
  // 导入设备弹窗相关状态
  const [isImportModalVisible, setIsImportModalVisible] = useState(false);
  const [importingRecord, setImportingRecord] = useState<DeviceBatchData | null>(null);
  const [importForm] = Form.useForm();
  const [importLoading, setImportLoading] = useState(false);
  const [fileList, setFileList] = useState<any[]>([]);
  
  // 导入结果相关状态
  const [isResultModalVisible, setIsResultModalVisible] = useState(false);
  const [importResult, setImportResult] = useState<ImportResult | null>(null);

  // 关联套餐相关状态
  const [isLinkPackageModalVisible, setIsLinkPackageModalVisible] = useState(false);
  const [linkingRecord, setLinkingRecord] = useState<DeviceBatchData | null>(null);
  const [selectedPayPackageGroupId, setSelectedPayPackageGroupId] = useState<number | undefined>(undefined);
  const [selectedGiftPackageGroupId, setSelectedGiftPackageGroupId] = useState<number | undefined>(undefined);
  const [linkPackageLoading, setLinkPackageLoading] = useState(false);

  // 套餐列表 Drawer 相关状态
  const [isPackageDrawerVisible, setIsPackageDrawerVisible] = useState(false);
  const [packageDrawerTitle, setPackageDrawerTitle] = useState('');
  const [packageList, setPackageList] = useState<Package[]>([]);
  const [packageListLoading, setPackageListLoading] = useState(false);

  // 加载设备类型列表
  useEffect(() => {
    loadDeviceTypes();
  }, []);

  // 加载数据
  useEffect(() => {
    if (user) {
      loadData();
    }
  }, [pagination.current, pagination.pageSize, searchParams, user]);

  const loadDeviceTypes = async () => {
    try {
      const response = await deviceService.getAllDeviceTypes();
      if (response.data) {
        setDeviceTypes(response.data);
      }
    } catch (error) {
      console.error('加载设备类型失败:', error);
    }
  };

  const loadData = async () => {
    if (!user) return;

    try {
      setLoading(true);
      const response = await deviceService.getDeviceBatchList({
        adminUserId: user.userId,
        pageNo: pagination.current,
        pageSize: pagination.pageSize,
        ...searchParams,
      });

      if (response.data) {
        setDataSource(response.data.content || []);
        setPagination(prev => ({
          ...prev,
          total: response.data.totalElements || 0,
        }));
      }
    } catch (error) {
      console.error('加载设备批次失败:', error);
    } finally {
      setLoading(false);
    }
  };

  const columns: ColumnsType<DeviceBatchData> = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
    },
    {
      title: '批次名称',
      dataIndex: 'batchName',
      key: 'batchName',
    },
    {
      title: '设备类型',
      dataIndex: 'deviceTypeName',
      key: 'deviceTypeName',
    },
    {
      title: '经销商',
      dataIndex: 'dealerName',
      key: 'dealerName',
      width: 150,
    },
    {
      title: '设备数量',
      dataIndex: 'deviceCount',
      key: 'deviceCount',
      width: 100,
      render: (count: number) => (
        <span style={{ fontWeight: 'bold' }}>{count || 0}</span>
      ),
    },
    // {
    //   title: '状态',
    //   dataIndex: 'batchStatus',
    //   key: 'batchStatus',
    //   render: (status: string) => {
    //     const statusMap: Record<string, { text: string; color: string }> = {
    //       '0': { text: '待入库', color: 'default' },
    //       '1': { text: '已入库', color: 'blue' },
    //       '2': { text: '分发中', color: 'orange' },
    //       '3': { text: '已完成', color: 'green' },
    //     };
    //     const statusInfo = statusMap[status] || { text: status, color: 'default' };
    //     return <Tag color={statusInfo.color}>{statusInfo.text}</Tag>;
    //   },
    // },
    {
      title: '免费套餐组',
      dataIndex: 'giftPackageGroupName',
      key: 'giftPackageGroupName',
      width: 150,
      ellipsis: true,
      render: (text: string, record: DeviceBatchData) => {
        if (!text || !record.giftPackageGroupId) {
          return '-';
        }
        return (
          <a onClick={() => handleViewPackageList(record.giftPackageGroupId!, text)}>
            {text}
          </a>
        );
      },
    },
    {
      title: '付费套餐组',
      dataIndex: 'packageGroupName',
      key: 'packageGroupName',
      width: 150,
      ellipsis: true,
      render: (text: string, record: DeviceBatchData) => {
        if (!text || !record.payPackageGroupId) {
          return '-';
        }
        return (
          <a onClick={() => handleViewPackageList(record.payPackageGroupId!, text)}>
            {text}
          </a>
        );
      },
    },
    {
      title: '创建时间',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 200,
      render: (text: string) => text ? new Date(text).toLocaleString('zh-CN') : '-',
    },
    {
      title: '操作',
      key: 'action',
      width: 430,
      fixed: 'right',
      render: (_, record: DeviceBatchData) => (
        <Space size="small">
          {/* <Button 
            type="link" 
            size="small" 
            icon={<FileTextOutlined />}
            onClick={() => handleViewDetail(record)}
          >
            详情
          </Button> */}
          <Button 
            type="link" 
            size="small" 
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          >
            编辑
          </Button>

          <Button 
            type="link" 
            size="small" 
            icon={<FileTextOutlined />}
            onClick={() => handleImportDevice(record)}
          >
            导入设备
          </Button>
          <Button
            type="link"
            size="small"
            icon={<SyncOutlined />}
            onClick={() => handleUpdateCount(record)}
          >
            更新设备数量
          </Button>
          <Button
            type="link"
            size="small"
            icon={<LinkOutlined />}
            onClick={() => handleLinkPackage(record)}
          >
            关联套餐
          </Button>
          {/* <Button
            type="link" 
            size="small" 
            danger
            icon={<DeleteOutlined />}
            onClick={() => handleDelete(record)}
            disabled={record.deleted}
          >
            删除
          </Button> */}
        </Space>
      ),
    },
  ];

  const handleSearch = (value: string) => {
    setSearchParams((prev: any) => ({ ...prev, batchName: value }));
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  const handleFilterChange = (key: string, value: any) => {
    setSearchParams((prev: any) => ({ ...prev, [key]: value }));
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  const handleRefresh = () => {
    loadData();
  };

  const handleAdd = () => {
    setEditingRecord(null);
    form.resetFields();
    setIsModalVisible(true);
  };

  const handleEdit = (record: DeviceBatchData) => {
    setEditingRecord(record);
    form.setFieldsValue({
      batchName: record.batchName,
      batchStatus: record.batchStatus,
      deviceTypeId: record.deviceTypeId,
      dealerId: record.dealerId,
      payPackageGroupId: record.payPackageGroupId,
      deviceCount: record.deviceCount,
      config: record.config,
    });
    setIsModalVisible(true);
  };

  const handleDelete = async (record: DeviceBatchData) => {
    Modal.confirm({
      title: '确认删除',
      content: `确定要删除批次"${record.batchName}"吗？`,
      okText: '确定',
      cancelText: '取消',
      onOk: async () => {
        try {
          const response = await deviceService.deleteDeviceBatch(record.id);
          // 只有当后端返回成功状态时才显示成功消息
          if (response.status === 1) {
            message.success('删除成功');
          }
          // 无论成功与否都刷新数据，因为逻辑删除可能已经生效
          loadData();
        } catch (error) {
          // 这里只处理网络错误等异常情况
          console.error('删除失败:', error);
        }
      },
    });
  };

  const handleViewDetail = (record: DeviceBatchData) => {
    Modal.info({
      title: '批次详情',
      width: 700,
      content: (
        <div style={{ marginTop: 16 }}>
          <p><strong>批次ID：</strong>{record.id}</p>
          <p><strong>批次名称：</strong>{record.batchName}</p>
          <p><strong>设备类型：</strong>{record.deviceTypeName}</p>
          <p><strong>经销商：</strong>{record.dealerName}</p>
          <p><strong>设备数量：</strong>{record.deviceCount || 0}</p>
          <p><strong>付费套餐组：</strong>{record.packageGroupName || '无'}</p>
          {/* <p><strong>批次状态：</strong>{record.batchStatus}</p> */}
          <p><strong>配置信息：</strong>{record.config || '无'}</p>
          <p><strong>创建时间：</strong>{record.createdAt ? new Date(record.createdAt).toLocaleString('zh-CN') : '-'}</p>
          <p><strong>更新时间：</strong>{record.updatedAt ? new Date(record.updatedAt).toLocaleString('zh-CN') : '-'}</p>
        </div>
      ),
      okText: '关闭',
    });
  };

  const handleImportDevice = (record: DeviceBatchData) => {
    setImportingRecord(record);
    importForm.setFieldsValue({
      dealerName: record.dealerName,
      batchName: record.batchName,
      deviceTypeName: record.deviceTypeName,
    });
    setFileList([]);
    setIsImportModalVisible(true);
  };

  const handleUpdateCount = async (record: DeviceBatchData) => {
    try {
      const response = await deviceService.updateDeviceBatchCount(record.id);
      if (response.status === 1) {
        message.success(`设备数量更新成功，当前数量：${response.data}`);
        loadData(); // 刷新列表以显示更新后的数量
      } else {
        message.error(response.msg || '更新设备数量失败');
      }
    } catch (error: any) {
      console.error('更新设备数量失败:', error);
      message.error(error.message || '更新设备数量失败');
    }
  };

  // 查看套餐列表
  const handleViewPackageList = async (groupId: number, groupName: string) => {
    setPackageDrawerTitle(groupName);
    setIsPackageDrawerVisible(true);
    setPackageListLoading(true);
    try {
      const packages = await packageService.getPackagesByGroup(groupId);
      console.log(packages);
      setPackageList(packages?.content || []);
    } catch (error) {
      console.error('加载套餐列表失败:', error);
      message.error('加载套餐列表失败');
      setPackageList([]);
    } finally {
      setPackageListLoading(false);
    }
  };

  // 打开关联套餐弹窗
  const handleLinkPackage = (record: DeviceBatchData) => {
    setLinkingRecord(record);
    setSelectedPayPackageGroupId(record.payPackageGroupId);
    setSelectedGiftPackageGroupId(record.giftPackageGroupId);
    setIsLinkPackageModalVisible(true);
  };

  // 确认关联套餐
  const handleLinkPackageOk = async () => {
    if (!linkingRecord || !user) return;

    try {
      setLinkPackageLoading(true);
      const response = await deviceService.updateDeviceBatch(linkingRecord.id, {
        batchName: linkingRecord.batchName,
        batchStatus: linkingRecord.batchStatus,
        deviceTypeId: linkingRecord.deviceTypeId,
        dealerId: linkingRecord.dealerId,
        giftPackageGroupId: selectedGiftPackageGroupId,
        payPackageGroupId: selectedPayPackageGroupId,
        deviceCount: linkingRecord.deviceCount,
        config: linkingRecord.config,
      });

      if (response.status === 1) {
        message.success('关联套餐成功');
        setIsLinkPackageModalVisible(false);
        setLinkingRecord(null);
        setSelectedPayPackageGroupId(undefined);
        setSelectedGiftPackageGroupId(undefined);
        loadData();
      } else {
        message.error(response.msg || '关联套餐失败');
      }
    } catch (error: any) {
      console.error('关联套餐失败:', error);
      message.error(error.message || '关联套餐失败');
    } finally {
      setLinkPackageLoading(false);
    }
  };

  // 取消关联套餐
  const handleLinkPackageCancel = () => {
    setIsLinkPackageModalVisible(false);
    setLinkingRecord(null);
    setSelectedPayPackageGroupId(undefined);
    setSelectedGiftPackageGroupId(undefined);
  };

  const handleImportModalOk = async () => {
    if (!importingRecord || fileList.length === 0) {
      message.error('请选择要导入的Excel文件');
      return;
    }

    try {
      setImportLoading(true);
      const file = fileList[0].originFileObj;
      const response = await deviceService.importDevices(file, importingRecord.deviceTypeId, importingRecord.id);
      
      if (response.status === 1) {
        // 导入成功或部分成功
        setImportResult(response.data);
        setIsImportModalVisible(false);
        setIsResultModalVisible(true);
        importForm.resetFields();
        setFileList([]);
        loadData(); // 刷新批次列表，更新设备数量
        
        // 根据结果显示不同的消息
        const { successCount, updatedCount, failedCount } = response.data;
        if (failedCount === 0) {
          if (updatedCount === 0) {
            // 全部新增成功
            message.success(`导入成功，共导入 ${successCount} 个设备`);
          } else if (successCount === 0) {
            // 全部更新成功
            message.success(`导入完成，更新 ${updatedCount} 个设备`);
          } else {
            // 新增+更新成功
            message.success(`导入完成，新增 ${successCount} 个，更新 ${updatedCount} 个`);
          }
        } else {
          // 部分失败
          const parts = [];
          if (successCount > 0) parts.push(`新增 ${successCount} 个`);
          if (updatedCount > 0) parts.push(`更新 ${updatedCount} 个`);
          parts.push(`失败 ${failedCount} 个`);
          message.warning(`导入完成，${parts.join('，')}`);
        }
      } else {
        // 导入失败
        message.error(response.msg || '设备导入失败');
      }
    } catch (error: any) {
      console.error('设备导入失败:', error);
      message.error(error.message || '设备导入失败');
    } finally {
      setImportLoading(false);
    }
  };

  const handleImportModalCancel = () => {
    setIsImportModalVisible(false);
    importForm.resetFields();
    setFileList([]);
  };

  const handleResultModalCancel = () => {
    setIsResultModalVisible(false);
    setImportResult(null);
  };

  const handleDownloadFailedExcel = () => {
    if (!importResult?.failedExcelUrl) return;
    
    try {
      // 创建下载链接
      const link = document.createElement('a');
      link.href = importResult.failedExcelUrl;
      link.download = `导入失败设备_${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      message.success('失败设备Excel文件下载成功');
    } catch (error) {
      console.error('下载失败:', error);
      message.error('下载失败，请重试');
    }
  };

  const handleFileChange = (info: any) => {
    let newFileList = [...info.fileList];
    
    // 只保留最后一个文件
    newFileList = newFileList.slice(-1);
    
    // 验证文件类型
    newFileList = newFileList.map(file => {
      if (file.originFileObj) {
        const fileName = file.originFileObj.name;
        const isSupported = fileName.endsWith('.xlsx') || fileName.endsWith('.xls') || fileName.endsWith('.csv');
        if (!isSupported) {
          message.error('只能上传Excel文件(.xlsx或.xls格式)或CSV文件(.csv格式)');
          return null;
        }
      }
      return file;
    }).filter(Boolean);
    
    setFileList(newFileList);
  };

  const beforeUpload = (file: File) => {
    const isSupported = file.name.endsWith('.xlsx') || file.name.endsWith('.xls') || file.name.endsWith('.csv');
    if (!isSupported) {
      message.error('只能上传Excel文件(.xlsx或.xls格式)或CSV文件(.csv格式)');
      return false;
    }
    const isLt10M = file.size / 1024 / 1024 < 10;
    if (!isLt10M) {
      message.error('文件大小不能超过10MB');
      return false;
    }
    return false; // 阻止自动上传
  };

  const handleModalOk = async () => {
    if (!user) return;

    try {
      const values = await form.validateFields();
      const data: DeviceBatchRequest = {
        batchName: values.batchName,
        batchStatus: values.batchStatus || '1',
        deviceTypeId: values.deviceTypeId,
        dealerId: values.dealerId || user.dealerId,
        payPackageGroupId: values.payPackageGroupId,
        deviceCount: values.deviceCount,
        config: values.config,
      };

      if (editingRecord) {
        await deviceService.updateDeviceBatch(editingRecord.id, data);
        message.success('修改成功');
      } else {
        await deviceService.createDeviceBatch(data);
        message.success('新增成功');
      }

      setIsModalVisible(false);
      form.resetFields();
      loadData();
    } catch (error: any) {
      if (error.errorFields) {
        return;
      }
      console.error('操作失败:', error);
    }
  };

  const handleModalCancel = () => {
    setIsModalVisible(false);
    form.resetFields();
  };

  const handleTableChange = (newPagination: any) => {
    setPagination({
      current: newPagination.current,
      pageSize: newPagination.pageSize,
      total: pagination.total,
    });
  };

  return (
    <div>
      <Card>
        <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
          <Col>
            <Title level={3}>设备批次管理</Title>
          </Col>
          <Col>
            <Space>
              <Button icon={<PlusOutlined />} type="primary" onClick={handleAdd}>
                新增批次
              </Button>
              <Button icon={<ReloadOutlined />} onClick={handleRefresh}>
                刷新
              </Button>
            </Space>
          </Col>
        </Row>

        <Row justify="space-between" style={{ marginBottom: 16 }}>
          <Col>
            <Space>
              <Search
                placeholder="搜索批次名称"
                allowClear
                enterButton={<SearchOutlined />}
                style={{ width: 300 }}
                onSearch={handleSearch}
              />
              <Select 
                placeholder="设备类型" 
                style={{ width: 150 }} 
                allowClear
                onChange={(value) => handleFilterChange('deviceTypeId', value)}
              >
                {deviceTypes.map(type => (
                  <Option key={type.id} value={type.id}>{type.name}</Option>
                ))}
              </Select>
              {/* <Select 
                placeholder="批次状态" 
                style={{ width: 120 }} 
                allowClear
                onChange={(value) => handleFilterChange('batchStatus', value)}
              >
                <Option value="0">待入库</Option>
                <Option value="1">已入库</Option>
                <Option value="2">分发中</Option>
                <Option value="3">已完成</Option>
              </Select> */}
              {/* 超级经销商显示经销商筛选器 */}
              {user?.dealerType === 1 && (
                <DealerSelector
                  placeholder="筛选经销商"
                  style={{ width: 200 }}
                  allowClear
                  onChange={(value) => handleFilterChange('dealerId', value)}
                  status={1}
                />
              )}
            </Space>
          </Col>
          {/* <Col>
            <Space>
              <Button>导出数据</Button>
            </Space>
          </Col> */}
        </Row>

        <Table
          columns={columns}
          dataSource={dataSource}
          loading={loading}
          rowKey="id"
          scroll={{ x: 1400 }}
          onChange={handleTableChange}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: pagination.total,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => `第 ${range[0]}-${range[1]} 条/共 ${total} 条`,
          }}
        />
      </Card>

      <Modal
        title={editingRecord ? '编辑设备批次' : '新增设备批次'}
        open={isModalVisible}
        onOk={handleModalOk}
        onCancel={handleModalCancel}
        width={700}
        okText="确定"
        cancelText="取消"
      >
        <Form
          form={form}
          layout="vertical"
          initialValues={{
            batchStatus: '1',
            dealerId: user?.dealerId,
          }}
        >
          <Row gutter={16}>
            <Col span={24}>
              <Form.Item
                name="batchName"
                label="批次名称"
                rules={[{ required: true, message: '请输入批次名称' }]}
              >
                <Input placeholder="请输入批次名称" maxLength={255} />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="deviceTypeId"
                label="设备类型"
                rules={[{ required: true, message: '请选择设备类型' }]}
              >
                <Select placeholder="请选择设备类型">
                  {deviceTypes.map(type => (
                    <Option key={type.id} value={type.id}>{type.name} ({type.firm})</Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              {/* 超级经销商显示选择器，普通经销商使用当前用户的经销商ID */}
              {user?.dealerType === 1 ? (
                <Form.Item
                  name="dealerId"
                  label="经销商"
                  rules={[{ required: true, message: '请选择经销商' }]}
                >
                  <DealerSelector 
                    placeholder="请选择经销商"
                    status={1}
                  />
                </Form.Item>
              ) : (
                <Form.Item
                  name="dealerId"
                  label="经销商"
                  rules={[{ required: true, message: '经销商ID不能为空' }]}
                >
                  <Input 
                    disabled 
                    placeholder={user?.dealerName || '当前经销商'}
                    addonBefore="当前"
                  />
                </Form.Item>
              )}
            </Col>
          </Row>
        
          
          
          <Form.Item
            name="config"
            label="配置信息"
            rules={[
              {
                validator: (_, value) => {
                  if (!value) return Promise.resolve();
                  try {
                    JSON.parse(value);
                    return Promise.resolve();
                  } catch {
                    return Promise.reject(new Error('请输入有效的JSON格式'));
                  }
                },
              },
            ]}
          >
            <Input.TextArea 
              rows={3} 
              placeholder='请输入JSON格式的配置信息，例如：{"price":100,"warranty":"1年"}' 
            />
          </Form.Item>
        </Form>
      </Modal>

      {/* 导入设备弹窗 */}
      <Modal
        title="导入设备"
        open={isImportModalVisible}
        onOk={handleImportModalOk}
        onCancel={handleImportModalCancel}
        width={600}
        okText="确定导入"
        cancelText="取消"
        confirmLoading={importLoading}
      >
        <Form
          form={importForm}
          layout="vertical"
        >
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="dealerName"
                label="经销商"
              >
                <Input disabled placeholder="经销商名称" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="batchName"
                label="批次名称"
              >
                <Input disabled placeholder="批次名称" />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="deviceTypeName"
                label="设备类型"
              >
                <Input disabled placeholder="设备类型" />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item
            label="选择文件"
            required
            help="请选择包含设备信息的文件，支持Excel(.xlsx/.xls)和CSV(.csv)格式，文件大小不超过10MB"
          >
            <Upload
              fileList={fileList}
              onChange={handleFileChange}
              beforeUpload={beforeUpload}
              accept=".xlsx,.xls,.csv"
              maxCount={1}
            >
              <Button icon={<UploadOutlined />}>
                选择文件
              </Button>
            </Upload>
          </Form.Item>
        </Form>
      </Modal>

      {/* 导入结果弹窗 */}
      <Modal
        title="设备导入结果"
        open={isResultModalVisible}
        onCancel={handleResultModalCancel}
        width={800}
        footer={[
          <Button key="download" type="primary" onClick={handleDownloadFailedExcel} disabled={!importResult?.failedExcelUrl}>
            下载失败设备Excel
          </Button>,
          <Button key="close" onClick={handleResultModalCancel}>
            关闭
          </Button>
        ]}
      >
        {importResult && (
          <div>
            {/* 导入统计信息 */}
            <Card size="small" style={{ marginBottom: 16 }}>
              <Row gutter={16}>
                <Col span={5}>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: 24, fontWeight: 'bold', color: '#1890ff' }}>
                      {importResult.totalCount}
                    </div>
                    <div style={{ color: '#666' }}>总数量</div>
                  </div>
                </Col>
                <Col span={5}>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: 24, fontWeight: 'bold', color: '#52c41a' }}>
                      {importResult.successCount}
                    </div>
                    <div style={{ color: '#666' }}>新增</div>
                  </div>
                </Col>
                <Col span={4}>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: 24, fontWeight: 'bold', color: '#1890ff' }}>
                      {importResult.updatedCount}
                    </div>
                    <div style={{ color: '#666' }}>更新</div>
                  </div>
                </Col>
                <Col span={5}>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: 24, fontWeight: 'bold', color: '#ff4d4f' }}>
                      {importResult.failedCount}
                    </div>
                    <div style={{ color: '#666' }}>失败</div>
                  </div>
                </Col>
                <Col span={5}>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: 24, fontWeight: 'bold', color: '#faad14' }}>
                      {importResult.totalCount > 0 ? (((importResult.successCount + importResult.updatedCount) / importResult.totalCount) * 100).toFixed(2) : '0.00'}%
                    </div>
                    <div style={{ color: '#666' }}>成功率</div>
                  </div>
                </Col>
              </Row>
            </Card>

            {/* 错误详情 */}
            {importResult.errors.length > 0 && (
              <Card size="small" title="错误详情">
                <div style={{ maxHeight: 300, overflowY: 'auto' }}>
                  {importResult.errors.map((error: ImportError, index: number) => (
                    <div key={index} style={{ 
                      padding: '8px 12px', 
                      marginBottom: 8, 
                      backgroundColor: '#fff2f0', 
                      border: '1px solid #ffccc7',
                      borderRadius: 4 
                    }}>
                      <div style={{ fontWeight: 'bold', color: '#ff4d4f' }}>
                        设备编号: {error.deviceNum}
                      </div>
                      <div style={{ color: '#666', fontSize: 12, marginTop: 4 }}>
                        错误原因: {error.errorMessage}
                      </div>
                    </div>
                  ))}
                </div>
              </Card>
            )}

            {/* 成功提示 */}
            {importResult.failedCount === 0 && (
              <div style={{ 
                textAlign: 'center', 
                padding: '20px', 
                backgroundColor: '#f6ffed', 
                border: '1px solid #b7eb8f',
                borderRadius: 4,
                color: '#52c41a'
              }}>
                <div style={{ fontSize: 16, fontWeight: 'bold' }}>🎉 导入成功！</div>
                <div style={{ marginTop: 8 }}>
                  {importResult.updatedCount === 0 
                    ? `所有设备都已成功导入（共 ${importResult.successCount} 个）`
                    : importResult.successCount === 0
                      ? `所有设备批次已更新（共 ${importResult.updatedCount} 个）`
                      : `新增 ${importResult.successCount} 个设备，更新 ${importResult.updatedCount} 个设备批次`
                  }
                </div>
              </div>
            )}
          </div>
        )}
      </Modal>

      {/* 关联套餐弹窗 */}
      <Modal
        title="关联套餐"
        open={isLinkPackageModalVisible}
        onOk={handleLinkPackageOk}
        onCancel={handleLinkPackageCancel}
        confirmLoading={linkPackageLoading}
        okText="确定"
        cancelText="取消"
        width={500}
      >
        <div style={{ marginBottom: 16 }}>
          <p><strong>批次名称：</strong>{linkingRecord?.batchName}</p>
        </div>
        <Form.Item label="免费套餐组" style={{ marginBottom: 16 }}>
          <PackageGroupSelector
            value={selectedGiftPackageGroupId}
            onChange={(value) => setSelectedGiftPackageGroupId(value as number)}
            style={{ width: '100%' }}
            groupType="FREE"
            placeholder="请选择免费套餐组"
          />
        </Form.Item>
        <Form.Item label="付费套餐组" style={{ marginBottom: 0 }}>
          <PackageGroupSelector
            value={selectedPayPackageGroupId}
            onChange={(value) => setSelectedPayPackageGroupId(value as number)}
            style={{ width: '100%' }}
            groupType="PAY"
            placeholder="请选择付费套餐组"
          />
        </Form.Item>
      </Modal>

      {/* 套餐列表 Drawer */}
      <Drawer
        title={`套餐列表 - ${packageDrawerTitle}`}
        placement="right"
        width={500}
        open={isPackageDrawerVisible}
        onClose={() => setIsPackageDrawerVisible(false)}
      >
        <Table
          dataSource={packageList}
          loading={packageListLoading}
          rowKey="id"
          pagination={false}
          size="small"
          columns={[
            {
              title: '套餐名称',
              dataIndex: 'packageName',
              key: 'packageName',
            },
            {
              title: '原价',
              dataIndex: 'originalPrice',
              key: 'originalPrice',
              width: 80,
              render: (price: number) => `¥${price}`,
            },
            {
              title: '实际价格',
              dataIndex: 'actualPrice',
              key: 'actualPrice',
              width: 90,
              render: (price: number) => `¥${price}`,
            },
            {
              title: '时长',
              key: 'vipDuration',
              width: 80,
              render: (_: unknown, record: Package) => formatPackageDuration(record),
            },
            {
              title: '状态',
              dataIndex: 'status',
              key: 'status',
              width: 70,
              render: (status: number) => (
                <Tag color={status === 1 ? 'green' : 'red'}>
                  {status === 1 ? '启用' : '禁用'}
                </Tag>
              ),
            },
          ]}
        />
      </Drawer>
    </div>
  );
};

export default DeviceBatch;


